import {
  ApproveJobScopeChangeRequest,
  FinalizeJobScopeChangeRequest,
  GetJobScopeChangeRequest,
  GetJobScopeChangePriceBreakdownForAdminRequest,
  JobScopeChange,
  ListJobScopeChangesRequest,
  GetJobScopeChangeStatusAggregationsRequest,
  RejectJobScopeChangeRequest,
  JobScopeChangeStatusAggregations as ScopeChangeRequestStatusAggregationsProto,
  JobScopeChangePriceBreakdownForAdminResponse,
  EditJobScopeChangeItemsRequest,
} from '@taxfyle/api-internal/internal/job_scope_change_pb'
import { PrimeUser } from 'iam/UserStore'
import { PrimeWorkspace } from 'iam/WorkspaceStore'
import { collection } from 'infra/collection'
import { PrimeJob } from 'jobs/JobStore'
import { Id } from 'types/definitions'
import { numberToDecimal } from 'utils/grpc-util'
import ScopeChangeRequest, {
  ScopeChangeRequestPriceBreakDown,
} from './scope-change-requests/ScopeChangeRequest'
import ScopeChangeRequestByStatusCount from './scope-change-requests/ScopeChangeRequestStatusAggregation'
import { ScopeChangeRequestAPI } from './scope-change-requests/API'
import { BoolValue } from 'google-protobuf/google/protobuf/wrappers_pb'

export interface FindScopeChangeRequest {
  workspaceIds?: string[]
  filter?: string
  orderBy?: string
  pageSize?: number
  pageToken?: string
}

export default class ScopeChangeRequestStore {
  /**
   * Change requests collection.
   */
  scopeChangeRequests = collection<JobScopeChange.AsObject, ScopeChangeRequest>(
    {
      create: () =>
        new ScopeChangeRequest(
          this.primeWorkspace,
          this.primeUser,
          this.primeJob,
        ),
    },
  )

  scopeChangeRequestPriceBreakdowns = collection<
    JobScopeChangePriceBreakdownForAdminResponse.AsObject,
    ScopeChangeRequestPriceBreakDown
  >({
    create: () => new ScopeChangeRequestPriceBreakDown(),
  })

  scopeChangeRequestByStatus = collection<
    ScopeChangeRequestStatusAggregationsProto.AsObject,
    ScopeChangeRequestByStatusCount
  >({
    create: () => new ScopeChangeRequestByStatusCount(),
    getDataId: (json) => json.status,
    getModelId: (model) => model.status,
  })

  constructor(
    private scopeChangeRequestAPI: ScopeChangeRequestAPI,
    private primeWorkspace: PrimeWorkspace,
    private primeUser: PrimeUser,
    private primeJob: PrimeJob,
  ) {}

  /**
   * Fetch change requests from api
   * @returns
   */
  async searchScopeChangeRequests(query: FindScopeChangeRequest) {
    const request = new ListJobScopeChangesRequest()

    if (query.workspaceIds) {
      request.setWorkspaceIdsList(query.workspaceIds)
    }

    if (query.filter) {
      request.setFilter(query.filter)
    }

    if (query.orderBy) {
      request.setOrderBy(query.orderBy)
    }

    if (query.pageSize) {
      request.setPageSize(query.pageSize)
    }

    if (query.pageToken) {
      request.setPageToken(query.pageToken)
    }

    const result =
      await this.scopeChangeRequestAPI.listJobScopeChangeRequests(request)
    return {
      data: this.scopeChangeRequests.set(
        result.getJobScopeChangesList().map((op) => op.toObject()),
      ),
      total: result.getJobScopeChangesList().length,
      cursor: result.getNextPageToken(),
    }
  }

  /**
   * Fetch a single scope change request
   * @param scopeChangeRequestId
   */
  async fetchScopeChangeRequest(scopeChangeRequestId: string) {
    const getScopeChangeRequest = new GetJobScopeChangeRequest().setId(
      scopeChangeRequestId,
    )

    const updated = await this.scopeChangeRequestAPI.getJobScopeChange(
      getScopeChangeRequest,
    )

    this.scopeChangeRequests.set(updated.toObject())
  }

  /**
   * Fetch a single scope change request price breakdown
   * @param scopeChangeRequest
   * @param items
   */
  async fetchScopeChangeRequestPriceBreakDown(
    scopeChangeRequest: ScopeChangeRequest,
  ) {
    const getJobScopeChangePriceBreakdownForAdminRequest =
      new GetJobScopeChangePriceBreakdownForAdminRequest().setId(
        scopeChangeRequest.id,
      )

    const updated =
      await this.scopeChangeRequestAPI.getJobScopeChangePriceBreakdownForAdmin(
        getJobScopeChangePriceBreakdownForAdminRequest,
      )

    return this.scopeChangeRequestPriceBreakdowns.set(updated.toObject())
  }

  /**
   * Edit the scope change request
   */
  editScopeChangeRequest(
    scopeChangeRequestId: string,
    scopeChangeCustomItems: EditScopeChangeCustomItemParams[],
    scopeChangeInventoryItems: EditScopeChangeInventoryItemParams[],
  ) {
    const customScopeChangeItemsList = scopeChangeCustomItems.map((i) =>
      new EditJobScopeChangeItemsRequest.NewJobScopeChangeCustomItemRequest()
        .setName(i.name)
        .setQuantity(i.quantity)
        .setPrice(numberToDecimal(i.price))
        .setProPrice(numberToDecimal(i.proPrice)),
    )

    const inventoryItemsRequest = scopeChangeInventoryItems.map((i) =>
      new EditJobScopeChangeItemsRequest.NewJobScopeChangeInventoryItemRequest()
        .setVersionId(i.versionId)
        .setQuantity(i.quantity),
    )

    const request = new EditJobScopeChangeItemsRequest()
      .setJobScopeChangeId(Id.assert(scopeChangeRequestId))
      .setNewCustomScopeItemsList(customScopeChangeItemsList)
      .setNewInventoryScopeItemsList(inventoryItemsRequest)

    return this.scopeChangeRequestAPI.editJobScopeChange(request)
  }

  /**
   * Finalize scope change request
   */
  async finalizeScopeChangeRequest(
    scopeChangeRequest: ScopeChangeRequest,
    items: ScopeChangeCustomItem[],
  ) {
    const request = new FinalizeJobScopeChangeRequest()
      .setJobId(Id.assert(scopeChangeRequest.job.id))
      .setItemsList(
        items.map((item) =>
          new FinalizeJobScopeChangeRequest.FinalizeJobScopeChangeItem()
            .setId(item.id)
            .setPrice(numberToDecimal(item.price))
            .setProPrice(numberToDecimal(item.proPrice)),
        ),
      )

    await this.scopeChangeRequestAPI.finalizeJobScopeChange(request)

    await this.fetchScopeChangeRequest(scopeChangeRequest.id)
  }

  /**
   * Accept scope change request
   * @param scopeChangeRequest
   */
  async approveScopeChangeRequest(
    scopeChangeRequest: ScopeChangeRequest,
    skipClientApproval: boolean = false,
  ) {
    const request = new ApproveJobScopeChangeRequest().setJobId(
      Id.assert(scopeChangeRequest.job.id),
    )

    if (skipClientApproval) {
      const skipClientApprovalValue = new BoolValue().setValue(true)

      request.setSkipClientApproval(skipClientApprovalValue)
    }

    await this.scopeChangeRequestAPI.approveJobScopeChange(request)

    await this.fetchScopeChangeRequest(scopeChangeRequest.id)
  }

  /**
   * Accepts scope change request on behalf of client
   * @param scopeChangeRequest
   */
  async acceptScopeChangeRequestOnBehalfOfClient(
    scopeChangeRequest: ScopeChangeRequest,
  ) {
    const request = new ApproveJobScopeChangeRequest().setJobId(
      Id.assert(scopeChangeRequest.job.id),
    )

    await this.scopeChangeRequestAPI.approveJobScopeChangeOnBehalfOfClient(
      request,
    )

    await this.fetchScopeChangeRequest(scopeChangeRequest.id)
  }

  /**
   * Rejects scope change request on behalf of client
   * @param scopeChangeRequest
   * @param reason
   */
  async rejectScopeChangeRequestOnBehalfOfClient(
    scopeChangeRequest: ScopeChangeRequest,
    reason: string,
  ) {
    const request = new RejectJobScopeChangeRequest()
      .setJobId(Id.assert(scopeChangeRequest.job.id))
      .setReason(reason)

    await this.scopeChangeRequestAPI.rejectJobScopeChangeOnBehalfOfClient(
      request,
    )

    await this.fetchScopeChangeRequest(scopeChangeRequest.id)
  }

  /**
   * Reject scope change request
   * @param scopeChangeRequest
   * @param reason
   */
  async rejectScopeChangeRequest(
    scopeChangeRequest: ScopeChangeRequest,
    reason: string,
  ) {
    const request = new RejectJobScopeChangeRequest()
      .setJobId(Id.assert(scopeChangeRequest.job.id))
      .setReason(reason)

    await this.scopeChangeRequestAPI.rejectJobScopeChange(request)

    await this.fetchScopeChangeRequest(scopeChangeRequest.id)
  }

  /**
   * Fetch scope change requests by status
   * @param workspaces
   */
  async fetchScopeChangeRequestStatusAggregations(workspaces?: string[]) {
    let getScopeChangeRequestStatusAggregationsRequest =
      new GetJobScopeChangeStatusAggregationsRequest()

    if (workspaces?.length) {
      getScopeChangeRequestStatusAggregationsRequest =
        getScopeChangeRequestStatusAggregationsRequest.setWorkspaceIdsList(
          workspaces,
        )
    }

    const result =
      await this.scopeChangeRequestAPI.getScopeChangeRequestStatusAggregations(
        getScopeChangeRequestStatusAggregationsRequest,
      )

    this.scopeChangeRequestByStatus.reset(
      result
        .getJobScopeChangeStatusAggregationList()
        .map((scr) => scr.toObject()),
    )
  }
}

export interface ScopeChangeCustomItem {
  id: string
  price: number
  proPrice: number
}

/**
 * Params for editing job scope changes.
 */
export interface EditScopeChangeCustomItemParams {
  name: string
  quantity: number
  price: number
  proPrice: number
}

export interface EditScopeChangeInventoryItemParams {
  versionId: string
  quantity: number
}
